import { ref } from 'vue'
import { deleteFile, downloadFile, getFilesByRelation, getUploadUrls, uploadFileDirectly } from '@/service/files'
import { useAccountStore } from '@/stores/account'
import { filterValidFiles } from '@/utils/utils'

const files = ref<any[]>([])
const isUploading = ref(false)
const loadingFiles = ref(false)
const fileUrl = ref<string | null>(null)

export function useFiles() {
  const accountStore = useAccountStore()

  const handleFilesChange = async (
    filesEvent: any,
    type: string,
    relationId: string = '0000',
    relationType: string = 'ticket',
  ): Promise<any> => {
    if (!filesEvent?.length) return
    isUploading.value = true
    try {
      const filesArray = Array.from(filesEvent)

      const validFiles = filterValidFiles(filesArray as any[])

      if (!validFiles.length) return

      const folderMap: Record<string, string> = {
        ticket: 'tickets',
        customer: 'customers',
        organization: 'customers',
      }
      const folder = `${folderMap[relationType] || relationType}/${relationId}`

      const filesData = filesArray.map((file: any) => ({
        fileName: file.name,
        contentType: file.type,
        folder,
        size: file.size,
        relationId,
        relationType,
        createdBy: accountStore.user.id as string,
        type,
      }))

      const { onSuccess } = await getUploadUrls(filesData)

      return new Promise((resolve, reject) => {
        onSuccess(async (data) => {
          try {
            await uploadFileDirectly(data, filesEvent)
            files.value.push(
              ...filesData.map((fileData, index) => {
                return { ...fileData, id: data[index].fileId, mimetype: fileData.contentType }
              }),
            )
            resolve(data)
          } catch (error) {
            reject(error)
          }
        })
      })
    } finally {
      isUploading.value = false
    }
  }

  const fetchFiles = async (ticketId: any) => {
    loadingFiles.value = true
    try {
      const { onSuccess } = await getFilesByRelation(ticketId)

      onSuccess((data) => {
        files.value = data.files
      })
    } catch (error) {
      console.error('Error on getting files', error)
    } finally {
      loadingFiles.value = false
    }
  }

  const downloadFileHandler = async (file: any) => {
    try {
      const { onSuccess } = await downloadFile(file.id, true)

      onSuccess((response: any) => {
        const blob = response instanceof Blob ? response : new Blob([response], { type: 'application/octet-stream' })

        let fileName = file.fileName

        if (response.headers && response.headers['content-disposition']) {
          const disposition = response.headers['content-disposition']
          const fileNameMatch = disposition.match(/filename="(.+)"/)

          if (fileNameMatch && fileNameMatch.length === 2) {
            fileName = fileNameMatch[1]
          }
        }

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')

        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      })
    } catch (error) {
      console.error('Error on downloading file : ', error)
    }
  }

  const deleteFileHandler = async (fileId: any) => {
    try {
      const { onSuccess } = await deleteFile(fileId)

      onSuccess(() => {
        files.value = files.value.filter((file) => file.id !== fileId)
      })
    } catch (error) {
      console.error('Error on deleting file : ', error)
    }
  }

  return {
    files,
    isUploading,
    loadingFiles,
    fileUrl,
    handleFilesChange,
    fetchFiles,
    downloadFileHandler,
    deleteFileHandler,
  }
}
